import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Boat, Airplane, FileText, Cylinder, Stack, Warehouse, Truck, GlobeHemisphereEast,
  MagnifyingGlass, ArrowRight, Phone, EnvelopeSimple, MapPin, WhatsappLogo,
  CheckCircle, ShieldCheck, Clock, Users, Package, ChartLineUp,
} from "@phosphor-icons/react";
import { api, API_BASE } from "@/lib/api";
import { useBrand, BRAND_LOGO_URL } from "@/lib/brand";
import LiveTracking from "@/components/LiveTracking";

// ── Static brand data ──────────────────────────────────────────────────
const SERVICES = [
  { icon: Boat, title: "Sea Freight (FCL & LCL)", desc: "Competitive ocean rates across 100+ ports — port-to-port, door-to-door." },
  { icon: Airplane, title: "Air Freight", desc: "Express and consolidated airfreight for time-critical cargo worldwide." },
  { icon: FileText, title: "Customs Clearance (CHA)", desc: "Fast import & export customs clearance at every major Indian port — paperwork handled end-to-end." },
  { icon: Cylinder, title: "ISO Tank Logistics", desc: "Specialized chemical & food-grade liquid bulk movement, fleet-tracked end-to-end." },
  { icon: Stack, title: "Project Cargo", desc: "Over-dimensional, breakbulk & heavy-lift project logistics with full route planning." },
  { icon: Warehouse, title: "Warehousing & Distribution", desc: "Bonded & open warehousing, inventory and last-mile distribution." },
  { icon: Truck, title: "Door-to-Door Delivery", desc: "Single window — pickup at origin to delivery at consignee's door." },
  { icon: GlobeHemisphereEast, title: "Global Freight Management", desc: "Multimodal supply-chain orchestration across 50+ countries." },
];
const STATS = [
  { k: "100+", v: "Global Agent Partners" },
  { k: "5000+", v: "Shipments Managed" },
  { k: "50+", v: "Countries Served" },
  { k: "24/7", v: "Customer Support" },
];
const LANES = ["India ➜ Europe", "India ➜ Far East Asia", "India ➜ UAE & Gulf", "India ➜ Russia", "India ➜ USA"];
const ISO_FEATURES = [
  "Real-time Tank Availability Tracking",
  "Fleet Visibility & Position Monitoring",
  "Chemical & Hazmat Logistics",
  "Food-Grade Liquid Transportation",
  "Global Tank Inventory Management",
];
const OFFICES_FALLBACK = [
  { city: "Mumbai", role: "Head Office", lines: ["Unit 10, Bhoomi Acres, Phase 2", "Waghbil Road, Hiranandani Est", "Thane, Maharashtra 400 615, India"] },
  { city: "Jodhpur", role: "Branch", lines: ["Industrial Area Phase II", "Jodhpur, Rajasthan", "India"] },
  { city: "Jaipur", role: "Branch", lines: ["MI Road, Jaipur", "Rajasthan, India", ""] },
];
// Logo bundled with the app (served from frontend/public/logos/). Self-contained — no
// dependency on any external CDN. Place the file at public/logos/fidel-group.png.
const LOGO_URL = "/logos/fidel-group.png";
const CARRIER_PARTNERS = ["MAERSK", "MSC", "CMA CGM", "HAPAG-LLOYD", "ONE", "EVERGREEN", "ZIM", "HMM", "COSCO", "YANG MING"];
const TESTIMONIALS = [
  { who: "Rajiv Mehta", co: "Chemcorp Exports", note: "{BRAND} handles 30+ ISO tank movements for us every quarter. Zero incidents in 3 years." },
  { who: "Sara Khan", co: "Bharat Pharma", note: "Fastest customs clearance in Nhava Sheva. Their team picks up the phone at 11 PM." },
  { who: "Hiroshi Tanaka", co: "Osaka Trading", note: "Best India-to-Far-East rates we've seen — and a real human handling every shipment." },
];

// ── Tiny helpers ───────────────────────────────────────────────────────
const relativeTime = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} minute${diffSec < 120 ? "" : "s"} ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hour${diffSec < 7200 ? "" : "s"} ago`;
  const days = Math.floor(diffSec / 86400);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  return `${Math.floor(months / 12)} year${months < 24 ? "" : "s"} ago`;
};

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`px-6 md:px-12 lg:px-20 py-16 md:py-24 ${className}`}>{children}</section>
);
const SectionTitle = ({ eyebrow, title, sub }) => (
  <div className="mb-10">
    {eyebrow && <div className="text-xs font-semibold tracking-[0.18em] uppercase text-blue-600 mb-2">{eyebrow}</div>}
    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">{title}</h2>
    {sub && <p className="mt-3 text-slate-600 text-base md:text-lg max-w-2xl">{sub}</p>}
  </div>
);

export default function FidelLanding() {
  // ── Dynamic site settings (admin-editable) ───────────────────────────
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/site/settings").then(({ data }) => { if (!cancelled) setSettings(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // Brand identity for this domain (Fidel / JT / …). Drives logo, name, contact, offices.
  const brand = useBrand();
  // Prefer the brand's logo_url (a path like /logos/jt-shippings.png) — the
  // /public/brand/logo endpoint only serves a base64 logo_data, which most
  // companies don't have. Last resort is the bundled default, via onError.
  const logoSrc = brand?.logo_url || BRAND_LOGO_URL;
  const offices = brand?.offices?.length ? brand.offices : (settings?.offices?.length ? settings.offices : OFFICES_FALLBACK);
  const contactEmail = brand?.contact_email || settings?.contact_email || "info@fidellogistics.in";
  const contactPhone = brand?.contact_phone || settings?.contact_phone || "";
  const waNumber = (brand?.whatsapp_number || settings?.whatsapp_number || "").replace(/\D/g, "") || "919000000000";
  const telLink = contactPhone ? `tel:${contactPhone.replace(/\s/g, "")}` : null;
  // Display name for this domain. The default brand keeps its legal-style wording;
  // a host matched to a company renders under that company's own name throughout.
  const isDefaultBrand = !brand || brand.is_default;
  const brandName = isDefaultBrand ? "FIDEL GROUP (INDIA)" : brand.name;       // logo lockups
  const brandProse = isDefaultBrand ? "FIDEL Group (India)" : brand.name;      // inside sentences
  const brandTagline = brand?.tagline || "Precision in Logistics, Excellence in Service";
  // "Mumbai, Jodhpur, and Jaipur" — built from whichever offices this brand serves.
  const officeCities = offices.map((o) => o.city).filter(Boolean);
  const officeList = officeCities.length > 1
    ? `${officeCities.slice(0, -1).join(", ")}, and ${officeCities[officeCities.length - 1]}`
    : (officeCities[0] || "");

  // ── Track widget ─────────────────────────────────────────────────────
  const [ref, setRef] = useState("");
  const [tracking, setTracking] = useState(null);
  const [trackBusy, setTrackBusy] = useState(false);
  const doTrack = async (e) => {
    e?.preventDefault();
    if (!ref.trim()) { toast.error("Enter a BL / Container / AWB number"); return; }
    setTrackBusy(true);
    setTracking(null);
    try {
      const { data } = await api.get("/track/public", { params: { ref: ref.trim() } });
      setTracking(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not find that shipment");
    } finally {
      setTrackBusy(false);
    }
  };

  // Auto-refresh the tracking result every 45s so status changes appear live.
  useEffect(() => {
    if (!tracking?.reference) return undefined;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/track/public", { params: { ref: tracking.reference } });
        setTracking(data);
      } catch { /* transient — keep last known */ }
    }, 45000);
    return () => clearInterval(id);
  }, [tracking?.reference]);

  // ── Quote form ───────────────────────────────────────────────────────
  const [quote, setQuote] = useState({ name: "", company: "", email: "", phone: "", origin: "", destination: "", cargo_type: "", mode: "Sea", message: "" });
  const [quoteBusy, setQuoteBusy] = useState(false);
  const submitQuote = async (e) => {
    e.preventDefault();
    if (!quote.name || !quote.email) { toast.error("Name and email are required"); return; }
    setQuoteBusy(true);
    try {
      await api.post("/leads/freight-quote", quote);
      toast.success("Thanks! Our freight desk will reach out within 4 business hours.");
      setQuote({ name: "", company: "", email: "", phone: "", origin: "", destination: "", cargo_type: "", mode: "Sea", message: "" });
    } catch { toast.error(`Could not submit — please email ${contactEmail}`); }
    finally { setQuoteBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">
      {/* ── Top Nav ───────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="px-6 md:px-12 lg:px-20 h-20 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3">
            <img src={logoSrc} alt={brand?.name || "Logo"} onError={(e) => { const b = e.currentTarget; if (!b.dataset.fb) { b.dataset.fb = "1"; b.src = LOGO_URL; } else { b.style.display = "none"; } }} className="h-12 w-auto object-contain" />
            <div className="leading-tight hidden sm:block">
              <div className="font-bold text-slate-900 text-sm md:text-base">{brandName}</div>
              <div className="text-[10px] tracking-[0.18em] text-orange-500 -mt-0.5 font-semibold uppercase">{brandTagline}</div>
            </div>
          </a>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-700 font-medium">
            <a href="#services" className="hover:text-blue-700 transition-colors">Services</a>
            <a href="#tank" className="hover:text-blue-700 transition-colors">ISO Tank</a>
            <a href="#network" className="hover:text-blue-700 transition-colors">Network</a>
            <a href="#about" className="hover:text-blue-700 transition-colors">About</a>
            <a href="#contact" className="hover:text-blue-700 transition-colors">Contact</a>
          </nav>
          <div className="flex items-center gap-2">
            <a href="#quote" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors shadow-sm" data-testid="nav-get-quote-button">Get Quote <ArrowRight size={14}/></a>
          </div>
        </div>
      </header>

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-blue-900 text-white">
        <img src="https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg?auto=compress&cs=tinysrgb&w=1920&q=70" alt="Container ship at sea" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-blue-950/75 to-blue-900/40" />
        <div className="relative px-6 md:px-12 lg:px-20 pt-20 pb-24 md:pt-28 md:pb-36">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05]">
              Global Shipping & Freight Solutions From <span className="text-orange-400">India To The World</span>
            </h1>
            <p className="mt-6 text-base md:text-lg lg:text-xl text-blue-100/90 max-w-2xl">
              Sea Freight, Air Freight, Customs Clearance, ISO Tank Logistics & Global Supply Chain Solutions.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#quote" className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors shadow-lg shadow-orange-500/20" data-testid="hero-cta-quote">Get Freight Quote <ArrowRight size={16}/></a>
              <a href="#track" className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold backdrop-blur-sm transition-colors" data-testid="hero-cta-track"><MagnifyingGlass size={16}/> Track Shipment</a>
            </div>
            <div className="mt-10 flex flex-wrap gap-2">
              {LANES.map((l) => <span key={l} className="text-xs px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-blue-100">{l}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* ── Track widget ───────────────────────────────────────── */}
      <Section id="track" className="bg-slate-50 -mt-16 md:-mt-20 relative z-10">
        <div className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-xl shadow-xl shadow-slate-200/50 p-6 md:p-8">
          <div className="text-xs font-semibold tracking-[0.18em] uppercase text-blue-600 mb-2">Live Tracking</div>
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900">Where is your shipment?</h3>
          <form onSubmit={doTrack} className="mt-5 flex flex-col sm:flex-row gap-3" data-testid="track-form">
            <div className="flex-1 relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} placeholder="Enter BL / Container / AWB Number" className="w-full h-12 pl-10 pr-4 rounded-md bg-slate-50 border border-slate-200 text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="hero-track-shipment-input"/>
            </div>
            <button type="submit" disabled={trackBusy} className="h-12 px-6 rounded-md bg-blue-700 text-white font-semibold hover:bg-blue-800 disabled:opacity-50" data-testid="hero-track-shipment-button">{trackBusy ? "Searching…" : "Track Shipment"}</button>
          </form>
          {tracking && (
            <div className="mt-6 grid md:grid-cols-3 gap-4 text-sm" data-testid="tracking-results-container">
              <div className="md:col-span-3">
                <LiveTracking
                  theme="light"
                  status={tracking.status}
                  pol={tracking.pol_name}
                  pod={tracking.pod_name}
                  etd={tracking.etd}
                  eta={tracking.eta}
                  sob={(tracking.timeline || []).find((x) => /board/i.test(x.stage || ""))?.date}
                  vessel={tracking.vessel}
                  voyage={tracking.voyage}
                  carrier={tracking.carrier}
                  mode={tracking.mode}
                  legs={tracking.transhipments || []}
                  updatedAt={tracking.updated_at}
                />
              </div>
              {tracking.transhipments?.length > 0 && (
                <div className="md:col-span-3 p-4 rounded-lg bg-white border border-slate-200" data-testid="tracking-transhipments">
                  <div className="text-xs font-semibold text-slate-700 mb-3">Transhipment ({tracking.transhipments.length})</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <tr>
                          <th className="text-left py-2 pr-4 font-semibold">Port</th>
                          <th className="text-left py-2 pr-4 font-semibold">Connecting Vessel</th>
                          <th className="text-left py-2 pr-4 font-semibold">Voyage</th>
                          <th className="text-left py-2 pr-4 font-semibold">Arrival</th>
                          <th className="text-left py-2 pr-4 font-semibold">Departure</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tracking.transhipments.map((t, i) => (
                          <tr key={i} className="border-b border-slate-100 last:border-0">
                            <td className="py-2 pr-4 font-semibold text-slate-900">{t.port_name || "—"}</td>
                            <td className="py-2 pr-4 text-slate-700">{t.vessel_name || "—"}</td>
                            <td className="py-2 pr-4 text-slate-700">{t.voyage_number || "—"}</td>
                            <td className="py-2 pr-4 text-slate-700">{t.eta || "—"}</td>
                            <td className="py-2 pr-4 text-slate-700">{t.etd || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {tracking.containers?.length > 0 && (
                <div className="md:col-span-3 p-4 rounded-lg bg-white border border-slate-200" data-testid="tracking-containers">
                  <div className="text-xs font-semibold text-slate-700 mb-3">Containers ({tracking.containers.length})</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <tr>
                          <th className="text-left py-2 pr-4 font-semibold">Container No.</th>
                          <th className="text-left py-2 pr-4 font-semibold">Type</th>
                          <th className="text-left py-2 pr-4 font-semibold">Discharged at Dest.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tracking.containers.map((c, i) => (
                          <tr key={i} className="border-b border-slate-100 last:border-0">
                            <td className="py-2 pr-4 font-mono font-semibold text-slate-900">{c.container_number}</td>
                            <td className="py-2 pr-4 text-slate-700">{c.type || "—"}</td>
                            <td className="py-2 pr-4 text-slate-700">{c.discharged_date || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Section>

      {/* ── Trusted Carrier Partners ───────────────────────────── */}
      <section className="bg-white border-y border-slate-100 py-12" data-testid="trusted-carriers">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 text-center mb-8">Trusted by exporters · Booked with the world&rsquo;s top carriers</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5">
            {CARRIER_PARTNERS.map((name) => (
              <div key={name} className="text-slate-500 hover:text-slate-900 transition-colors text-sm sm:text-base md:text-lg font-bold tracking-wide font-display" style={{ fontFamily: "Outfit, sans-serif" }}>{name}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services ──────────────────────────────────────────── */}
      <Section id="services">
        <SectionTitle eyebrow="What We Do" title="End-to-end logistics, owned start to finish" sub="From booking to clearance to last-mile delivery, every link in your supply chain handled by one accountable team."/>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s) => (
            <div key={s.title} className="group p-6 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all" data-testid={`service-${s.title.split(" ")[0].toLowerCase()}`}>
              <div className="w-11 h-11 rounded-lg bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center mb-4 transition-colors">
                <s.icon size={22} weight="duotone" className="text-blue-700" />
              </div>
              <div className="font-semibold text-slate-900">{s.title}</div>
              <div className="text-sm text-slate-600 mt-1.5">{s.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── ISO Tank ──────────────────────────────────────────── */}
      <Section id="tank" className="bg-gradient-to-br from-slate-900 to-blue-950 text-white">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold tracking-[0.18em] uppercase text-blue-300 mb-2">Specialised Capability</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">ISO Tank Management & Chemical Cargo Logistics</h2>
            <p className="mt-4 text-blue-100/90 max-w-xl">Fleet-tracked tank movements, IMO-class hazmat handling and food-grade liquid transport — purpose-built for chemical exporters and traders moving bulk liquids worldwide.</p>
            <ul className="mt-6 space-y-3">
              {ISO_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm"><CheckCircle size={16} weight="fill" className="text-blue-300 flex-shrink-0"/> {f}</li>
              ))}
            </ul>
            <a href="#quote" className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-lg shadow-orange-500/20">Request ISO Tank Quote <ArrowRight size={14}/></a>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1605745341112-85968b19335b?w=900&q=80" alt="ISO tank container" className="rounded-xl border border-white/10 shadow-2xl"/>
            <div className="absolute -bottom-5 -left-5 px-4 py-3 rounded-lg bg-white text-slate-900 shadow-xl flex items-center gap-3 text-xs">
              <div className="w-9 h-9 rounded-md bg-blue-100 flex items-center justify-center"><Cylinder size={18} weight="duotone" className="text-blue-700"/></div>
              <div>
                <div className="font-bold">Live Fleet · 2400+ Tanks</div>
                <div className="text-slate-500">Tracked across 18 ports</div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── Stats ──────────────────────────────────────────────── */}
      <Section className="bg-blue-700 text-white">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {STATS.map((s) => (
            <div key={s.v}>
              <div className="text-4xl md:text-5xl font-bold">{s.k}</div>
              <div className="text-blue-100 text-sm mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Network ────────────────────────────────────────────── */}
      <Section id="network">
        <SectionTitle eyebrow="Global Network" title="100+ countries · One accountable partner" sub="Owned-office presence across India and a curated agent network in every major trading hub from Rotterdam to Long Beach."/>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { r: "Europe", ports: "Rotterdam · Hamburg · Antwerp · Felixstowe · Genoa", icon: GlobeHemisphereEast },
            { r: "Russia & CIS", ports: "Novorossiysk · St. Petersburg · Vladivostok · Almaty · Tashkent", icon: Truck },
            { r: "Far East Asia", ports: "Shanghai · Singapore · Hong Kong · Busan · Tokyo", icon: Boat },
            { r: "Gulf", ports: "Jebel Ali · Dammam · Doha · Sohar · Kuwait · Bahrain", icon: ShieldCheck },
            { r: "USA & Canada", ports: "New York · Long Beach · Houston · Savannah · Vancouver", icon: Airplane },
            { r: "Africa", ports: "Durban · Mombasa · Lagos · Casablanca", icon: MapPin },
            { r: "Australia & NZ", ports: "Sydney · Melbourne · Auckland · Brisbane", icon: ChartLineUp },
          ].map((r) => (
            <div key={r.r} className="p-5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:shadow-md transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-white border border-slate-200 flex items-center justify-center"><r.icon size={20} weight="duotone" className="text-blue-700"/></div>
                <div className="font-bold text-slate-900">{r.r}</div>
              </div>
              <div className="mt-3 text-xs text-slate-600">{r.ports}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Quote ──────────────────────────────────────────────── */}
      <Section id="quote" className="bg-slate-50">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2">
            <SectionTitle eyebrow="Get a Freight Quote" title="The best rate. In 4 hours." sub="Tell us where it's going. Our freight desk reverts with rates, transit time and the right service mode — usually within four business hours."/>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3"><Clock size={18} className="text-blue-700"/> <span className="text-slate-700">Quote response within 4 business hours</span></div>
              <div className="flex items-center gap-3"><Users size={18} className="text-blue-700"/> <span className="text-slate-700">A real human, not a chatbot</span></div>
              <div className="flex items-center gap-3"><Package size={18} className="text-blue-700"/> <span className="text-slate-700">FCL, LCL, Air, ISO Tank — all modes covered</span></div>
            </div>
          </div>
          <form onSubmit={submitQuote} className="lg:col-span-3 bg-white p-6 md:p-8 rounded-xl border border-slate-200 shadow-sm" data-testid="quote-form">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                ["name", "Full Name *"], ["company", "Company"], ["email", "Email *"], ["phone", "Phone"],
                ["origin", "Origin (POL)"], ["destination", "Destination (POD)"], ["cargo_type", "Cargo Type"],
              ].map(([k, lbl]) => (
                <div key={k} className={k === "name" || k === "email" ? "" : ""}>
                  <label className="text-xs font-medium text-slate-600 block mb-1">{lbl}</label>
                  <input value={quote[k]} onChange={(e) => setQuote({...quote, [k]: e.target.value})} className="w-full h-10 px-3 rounded-md bg-white border border-slate-200 text-slate-900 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid={k === "name" ? "quote-form-name-input" : k === "email" ? "quote-form-email-input" : `quote-${k}`}/>
                </div>
              ))}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Mode</label>
                <select value={quote.mode} onChange={(e) => setQuote({...quote, mode: e.target.value})} className="w-full h-10 px-3 rounded-md bg-white border border-slate-200 text-sm" data-testid="quote-mode">
                  <option>Sea</option><option>Air</option><option>ISO Tank</option><option>Project Cargo</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-slate-600 block mb-1">Message</label>
                <textarea rows={3} value={quote.message} onChange={(e) => setQuote({...quote, message: e.target.value})} className="w-full px-3 py-2 rounded-md bg-white border border-slate-200 text-slate-900 text-sm focus:outline-none focus:border-blue-500" data-testid="quote-message"/>
              </div>
            </div>
            <button type="submit" disabled={quoteBusy} className="mt-5 w-full h-12 rounded-md bg-orange-500 hover:bg-orange-600 text-white font-semibold disabled:opacity-50 shadow-lg shadow-orange-500/20" data-testid="quote-form-submit-button">{quoteBusy ? "Sending…" : "Request Best Freight Rate"}</button>
          </form>
        </div>
      </Section>

      {/* ── Testimonials ──────────────────────────────────────── */}
      <Section>
        <SectionTitle eyebrow="Clients" title="Trusted by exporters & manufacturers"/>
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t) => (
            <div key={t.who} className="p-6 rounded-xl border border-slate-200 bg-white">
              <p className="text-sm text-slate-700 italic">&ldquo;{t.note.replace("{BRAND}", brandProse)}&rdquo;</p>
              <div className="mt-4 text-xs">
                <div className="font-bold text-slate-900">{t.who}</div>
                <div className="text-slate-500">{t.co}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── About ──────────────────────────────────────────────── */}
      <Section id="about" className="bg-slate-50">
        <SectionTitle eyebrow="About Us" title="ISO Tank Operator & Global Logistics Solutions Provider"/>
        <div className="grid lg:grid-cols-3 gap-10 items-start">
          <div className="lg:col-span-2 space-y-5 text-slate-700 text-base md:text-[17px] leading-relaxed">
            <p>
              <strong className="text-slate-900">{brandProse}</strong> is a specialized <strong className="text-slate-900">ISO Tank Operator and Global Logistics Solutions Provider</strong>, delivering safe, reliable, and cost-effective transportation of liquid bulk cargo across international markets.
            </p>
            <p>
              With a strong focus on ISO tank operations, we manage and coordinate the movement of food-grade, chemical, petrochemical, and specialty liquid products through our extensive tank inventory and global partner network. Our expertise ensures efficient equipment positioning, cargo safety, regulatory compliance, and seamless door-to-door logistics solutions.
            </p>
            <p>
              Building on our strengths in ISO tank logistics, we offer comprehensive <strong className="text-slate-900">freight forwarding services</strong> by sea and air, connecting India with key trade lanes across <strong className="text-slate-900">Europe, the Far East, the Gulf region, Russia, and other global destinations</strong>. Leveraging strong relationships with shipping lines, airlines, and international agents, we provide competitive freight rates, dependable transit schedules, and complete shipment visibility.
            </p>
            <p>
              To further support our clients&rsquo; supply chains, we provide <strong className="text-slate-900">Custom House Agent (CHA) and customs clearance services</strong>, ensuring smooth import and export processes through efficient documentation, regulatory compliance, and timely cargo clearance.
            </p>
            <p>
              With offices in <strong className="text-slate-900">{officeList}</strong>, supported by a trusted worldwide agent network, {brandProse} delivers integrated logistics solutions tailored to the evolving needs of global trade.
            </p>
            <p className="text-slate-600 italic border-l-4 border-orange-500 pl-4 mt-6">
              At {brandProse}, our commitment is to provide safe, compliant, and efficient logistics solutions that help our customers move their cargo confidently across the world.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="text-xs font-semibold tracking-[0.18em] uppercase text-orange-500 mb-3">Our Core Services</div>
            <ul className="space-y-3 text-sm text-slate-700">
              {[
                "ISO Tank Operations & Liquid Bulk Logistics",
                "Global Freight Forwarding (Sea & Air)",
                "Import & Export Logistics Management",
                "Customs Clearance & CHA Services",
                "Door-to-Door Transportation Solutions",
                "Project & Specialized Cargo Handling",
                "Global Agent Network Support",
              ].map((s) => (
                <li key={s} className="flex items-start gap-2.5">
                  <CheckCircle size={18} weight="fill" className="text-orange-500 flex-shrink-0 mt-0.5"/>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
            <img src="https://images.unsplash.com/photo-1568438350562-2cae6d394ad0?w=600&q=70" alt="Aerial port view" className="mt-6 rounded-lg w-full"/>
          </div>
        </div>
      </Section>

      {/* ── Contact ────────────────────────────────────────────── */}
      <Section id="contact">
        <SectionTitle eyebrow="Get in touch" title={`${offices.length} office${offices.length > 1 ? "s" : ""} in India · Agents in 100+ countries`}/>
        <div className="grid md:grid-cols-3 gap-5 mb-8" data-testid="contact-offices">
          {offices.map((o) => (
            <div key={`${o.city}-${o.role}`} className="p-5 rounded-xl border border-slate-200 bg-white">
              <div className="text-xs font-semibold tracking-wider uppercase text-blue-700">{o.role || "Office"}</div>
              <div className="text-xl font-bold text-slate-900 mt-1">{o.city}</div>
              <div className="mt-3 space-y-1 text-sm text-slate-600">
                {(o.lines || []).filter(Boolean).map((ln, i) => <div key={i} className={i === 0 ? "flex items-start gap-2" : ""}>{i === 0 && <MapPin size={14} className="text-slate-400 mt-0.5 flex-shrink-0"/>}<span>{ln}</span></div>)}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium" data-testid="contact-whatsapp"><WhatsappLogo size={18} weight="fill"/> Chat on WhatsApp</a>
          <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 hover:bg-slate-800 text-white font-medium" data-testid="contact-email"><EnvelopeSimple size={18} weight="fill"/> {contactEmail}</a>
          {telLink && (
            <a href={telLink} className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-900 font-medium" data-testid="contact-phone"><Phone size={18} weight="fill"/> {contactPhone}</a>
          )}
          <a href="#quote" className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium" data-testid="contact-quote"><ArrowRight size={18}/> Request a Quote</a>
        </div>
      </Section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="bg-slate-950 text-slate-300 px-6 md:px-12 lg:px-20 py-12">
        <div className="grid md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-3">
              <img src={logoSrc} alt={brand?.name || "Logo"} onError={(e) => { const b = e.currentTarget; if (!b.dataset.fb) { b.dataset.fb = "1"; b.src = LOGO_URL; } else { b.style.display = "none"; } }} className="h-12 w-auto object-contain" />
              <div>
                <div className="font-bold text-white text-sm">{brandName}</div>
                <div className="text-[10px] tracking-[0.18em] text-orange-400 font-semibold uppercase">{brandTagline}</div>
              </div>
            </div>
            <p className="mt-3 text-slate-400 text-xs leading-relaxed">India&rsquo;s freight forwarding & customs clearance specialist. Sea · Air · CHA · ISO Tank · Project Cargo.</p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-white mb-3">Services</div>
            <ul className="space-y-1.5 text-xs">
              <li>Sea Freight (FCL/LCL)</li><li>Air Freight</li><li>Customs Clearance (CHA)</li><li>ISO Tank Logistics</li><li>Project Cargo</li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-white mb-3">Offices</div>
            <ul className="space-y-1.5 text-xs">
              {offices.slice(0, 4).map((o) => (
                <li key={`${o.city}-${o.role}`}>{o.city}{o.role === "Head Office" ? " (Head Office)" : ""}</li>
              ))}
              <li>Global Agent Network · 100+ countries</li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-white mb-3">Get In Touch</div>
            <ul className="space-y-1.5 text-xs">
              <li><a href={`mailto:${contactEmail}`} className="hover:text-orange-300">{contactEmail}</a></li>
              {contactPhone && <li><a href={telLink || "#"} className="hover:text-orange-300">{contactPhone}</a></li>}
              <li><a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="hover:text-emerald-300">WhatsApp Chat</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-slate-800 flex flex-col md:flex-row justify-between gap-3 text-[11px] text-slate-500">
          <div>© {new Date().getFullYear()} {brandProse} · All rights reserved.</div>
          <div className="flex gap-4">
            <a href="#about" className="hover:text-slate-300">About</a>
            <a href="#contact" className="hover:text-slate-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
